import './About.css';
import "../../components/Theme/Theme.css";
import { useEffect, useLayoutEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { fetchTimelineElements } from '../../backend/timeline_elements';
import { Industry, TimelineItem } from '../../../data_types';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { getIcon } from './icons';
import IndustryCarousel from '../../components/Carousel/Carousel';
import Carousel from '../../components/Carousel/Carousel';
import { fetchIndustries } from '../../backend/industries';
import { Link, useParams } from 'react-router-dom';
import IndustryCard from '../../components/IndustryCard/IndustryCard';
import IndustrySlide from '../../components/Slides/Industries/IndustrySlide';
import DeepDiveSlide from '../../components/Slides/DeepDive/DeepDiveSlide';
import { Helmet } from 'react-helmet';
import { BeatLoader } from 'react-spinners';

const About = () => {

  const [timelineData, setTimelineData] = useState<TimelineItem[]>([]);
  const [industryData, setIndustryData] = useState<Industry[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { industryId } = useParams<{ industryId: string }>();

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  });

  useEffect(() => {
    document.documentElement.setAttribute('color-scheme', 'dark');
  })

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  useEffect(() => {
    const mediaElements = Array.from(document.querySelectorAll("img, video"));
    let loadedCount = 0;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === mediaElements.length) {
        setIsLoading(false);
      }
    };

    if (mediaElements.length === 0) {
      setIsLoading(false);
    } else {
      mediaElements.forEach((el) => {
        if (el instanceof HTMLImageElement && el.complete) {
          checkAllLoaded();
        } else if (el instanceof HTMLVideoElement && el.readyState >= 3) {
          checkAllLoaded();
        } else {
          el.addEventListener("load", checkAllLoaded);
          el.addEventListener("error", checkAllLoaded);
        }
      });
    }

    return () => {
      mediaElements.forEach((el) => {
        el.removeEventListener("load", checkAllLoaded);
        el.removeEventListener("error", checkAllLoaded);
      });
    };
  }, []);

  useEffect(() => {
    const loadTimelineElements = async () => {
      try {
        const response = await fetchTimelineElements();
        setTimelineData(response);
      } catch (error: any) {
        setError('Failed to fetch timeline data');
        console.error(error.message);
      }
    };

    const loadIndustry = async () => {
      try {
        const data = await fetchIndustries();
        setIndustryData(data.industries);
      } catch (error) {
        console.error('Failed to fetch industry details', error);
      }
    };

    loadTimelineElements();
    loadIndustry();
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <BeatLoader
          color={"var(--accent-1--dark)"}
          loading={isLoading}
          size={150}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }

  return (
    <Box className='slide' id='about_slide'>
      <Helmet>
        <title>About | Billet Precision Ltd.</title>
        <meta name="description" content="Learn about Billet Precision's mission and commitment to quality in CNC machining services." />
        <meta property="og:title" content="About | Billet Precision" />
        <meta property="og:description" content="Learn about Billet Precision's mission and commitment to quality in CNC machining services." />
        <link rel="canonical" href="https://billetprecision.ca/about" />
      </Helmet>
      <Grid className='slide_container' id='about_slide_container'>
        <Grid item className='slide_content' id='slide-title'>
          About
        </Grid>
        <VerticalTimeline
          className='component_container'
          lineColor='var(--timeline-line-color)'
        >
          {timelineData.length ? timelineData.map((item) => (
            <VerticalTimelineElement
              key={item.displayId}
              className={`vertical-timeline-element--${item.type} timeline-element`}
              date={item.date}
              iconStyle={{
                background: "var(--timeline-element-icon-background-color)",
                color: "var(--timeline-element-icon-color)",
                boxShadow: "0 0 0 4px var(--timeline-element-icon-box-shadow),inset 0 2px 0 rgba(0,0,0,.08),0 3px 0 4px rgba(0,0,0,.05)",
              } as React.CSSProperties}
              icon={getIcon(item.type)}
              contentStyle={{
                background: "var(--timeline-element-content-background-color)",
                color: "var(--timeline-element-content-font-color)",
                boxShadow: "0 3px 0 var(--timeline-element-content-box-shadow)"
              } as React.CSSProperties}
              contentArrowStyle={{ borderRight: `7px solid var(--timeline-element-content-arrow-color)` }}
            >
              <h3 className="vertical-timeline-element-title">{item.title}</h3>
              <h4 className="vertical-timeline-element-subtitle">{item.subtitle}</h4>
              <p className="vertical-timeline-element-description">{item.description}</p>

              <span className="timeline-element-id">{item.displayId}</span>
            </VerticalTimelineElement>
          )) : (
            <p>{error || 'No timeline data available'}</p>
          )}
        </VerticalTimeline>
        {/* <Box className="about-industry-container">
          <Carousel slides={carouselSlides}/>
        </Box> */}
      </Grid>
      <DeepDiveSlide />
      <IndustrySlide />
    </Box>
  );
};

export default About;
